/**
 * Mastering Panel Component
 * LUFS metering, multi-band compression, stereo imaging, export presets
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { Slider } from '../../ui/slider';
import { Progress } from '../../ui/progress';
import {
  Volume2,
  Gauge,
  Activity,
  Radio,
  Download,
  ChevronDown,
  ChevronUp,
  X,
  Check,
  AlertCircle,
  Disc,
  Headphones,
  Music2,
  Settings2,
  RefreshCw,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { 
  LUFSMeter, 
  EXPORT_PRESETS, 
  getLUFSLabel, 
  getLUFSColor 
} from './MasteringTools';

// ==================== LUFS Meter Display ====================

const LUFSMeterDisplay = ({ readings, targetLUFS, className }) => {
  const getMeterHeight = (value) => {
    if (!isFinite(value) || value < -60) return 0;
    // Map -60 to 0 LUFS to 0% to 100%
    return Math.max(0, Math.min(100, ((value + 60) / 60) * 100));
  };
  
  return (
    <div className={cn("bg-zinc-800/50 rounded-lg p-3", className)}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-white">Loudness</span>
        <span className={cn(
          "text-xs font-mono",
          getLUFSColor(readings.integrated, targetLUFS)
        )}>
          {getLUFSLabel(readings.integrated)}
        </span>
      </div>
      
      {/* Meter bars */}
      <div className="flex gap-2 h-24 mb-3">
        {/* Momentary */}
        <div className="flex-1 flex flex-col items-center">
          <div className="flex-1 w-full bg-zinc-900 rounded-sm overflow-hidden relative">
            <motion.div
              className="absolute bottom-0 w-full bg-gradient-to-t from-emerald-500 to-emerald-400"
              style={{ height: `${getMeterHeight(readings.momentary)}%` }}
              transition={{ duration: 0.05 }}
            />
            {/* Target line */}
            {targetLUFS && (
              <div 
                className="absolute w-full h-0.5 bg-amber-400"
                style={{ bottom: `${getMeterHeight(targetLUFS)}%` }}
              />
            )}
          </div>
          <span className="text-[8px] text-zinc-500 mt-1">M</span>
        </div>
        
        {/* Short-term */}
        <div className="flex-1 flex flex-col items-center">
          <div className="flex-1 w-full bg-zinc-900 rounded-sm overflow-hidden relative">
            <motion.div
              className="absolute bottom-0 w-full bg-gradient-to-t from-cyan-500 to-cyan-400"
              style={{ height: `${getMeterHeight(readings.shortTerm)}%` }}
              transition={{ duration: 0.1 }}
            />
            {targetLUFS && (
              <div 
                className="absolute w-full h-0.5 bg-amber-400"
                style={{ bottom: `${getMeterHeight(targetLUFS)}%` }}
              />
            )}
          </div>
          <span className="text-[8px] text-zinc-500 mt-1">S</span>
        </div>
        
        {/* Integrated */}
        <div className="flex-1 flex flex-col items-center">
          <div className="flex-1 w-full bg-zinc-900 rounded-sm overflow-hidden relative">
            <motion.div
              className="absolute bottom-0 w-full bg-gradient-to-t from-indigo-500 to-indigo-400"
              style={{ height: `${getMeterHeight(readings.integrated)}%` }}
              transition={{ duration: 0.3 }}
            />
            {targetLUFS && (
              <div 
                className="absolute w-full h-0.5 bg-amber-400"
                style={{ bottom: `${getMeterHeight(targetLUFS)}%` }}
              />
            )}
          </div>
          <span className="text-[8px] text-zinc-500 mt-1">I</span>
        </div>
      </div>
      
      {/* Readings */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="text-[10px] text-zinc-400">Momentary</div>
          <div className="text-xs font-mono text-emerald-400">
            {getLUFSLabel(readings.momentary)}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-zinc-400">Short-term</div>
          <div className="text-xs font-mono text-cyan-400">
            {getLUFSLabel(readings.shortTerm)}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-zinc-400">True Peak</div>
          <div className={cn(
            "text-xs font-mono",
            readings.truePeak > -1 ? "text-red-400" : "text-zinc-300"
          )}>
            {isFinite(readings.truePeak) ? `${readings.truePeak.toFixed(1)} dB` : '--'}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== Multi-band Compressor UI ====================

const MultibandCompressorUI = ({ bands, onChange, className }) => {
  const [expanded, setExpanded] = useState(false);
  
  const bandColors = ['bg-red-500', 'bg-amber-500', 'bg-emerald-500', 'bg-cyan-500'];
  const bandNames = ['Low', 'Low-Mid', 'High-Mid', 'High'];
  
  return (
    <div className={cn("bg-zinc-800/50 rounded-lg p-3", className)}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between mb-2"
      >
        <span className="text-xs font-medium text-white">Multi-band Comp</span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      
      {/* Band indicators */}
      <div className="flex gap-1 mb-2">
        {bandNames.map((name, i) => (
          <div
            key={name}
            className={cn(
              "flex-1 h-1 rounded-full",
              bandColors[i],
              bands[i]?.enabled !== false ? 'opacity-100' : 'opacity-30'
            )}
          />
        ))}
      </div>
      
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="space-y-3 pt-2">
              {bandNames.map((name, i) => (
                <div key={name} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-zinc-400">{name}</span>
                    <span className="text-[9px] text-zinc-500 font-mono">
                      {bands[i]?.threshold?.toFixed(0) ?? -20} dB
                    </span>
                  </div>
                  <Slider
                    value={[bands[i]?.threshold ?? -20]}
                    min={-60}
                    max={0}
                    step={1}
                    onValueChange={([v]) => onChange?.(i, 'threshold', v)}
                    className="h-4"
                  />
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== Stereo Imager UI ====================

const StereoImagerUI = ({ width, onChange, className }) => {
  return (
    <div className={cn("bg-zinc-800/50 rounded-lg p-3", className)}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-white">Stereo Width</span>
        <span className="text-xs font-mono text-zinc-400">
          {(width * 100).toFixed(0)}%
        </span>
      </div>
      
      {/* Visual width indicator */}
      <div className="relative h-6 bg-zinc-900 rounded-lg overflow-hidden mb-3">
        <motion.div
          className="absolute inset-y-0 left-1/2 bg-gradient-to-r from-indigo-500/50 to-transparent"
          style={{ 
            width: `${Math.min(50, width * 50)}%`,
            transform: 'translateX(0)'
          }}
        />
        <motion.div
          className="absolute inset-y-0 right-1/2 bg-gradient-to-l from-indigo-500/50 to-transparent"
          style={{ 
            width: `${Math.min(50, width * 50)}%`,
          }}
        />
        <div className="absolute inset-y-0 left-1/2 w-px bg-zinc-600" />
        
        {/* L/R labels */}
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[8px] text-zinc-500">L</span>
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[8px] text-zinc-500">R</span>
      </div>
      
      <Slider
        value={[width]}
        min={0}
        max={2}
        step={0.01}
        onValueChange={([v]) => onChange?.(v)}
        className="h-4"
      />
      
      <div className="flex justify-between text-[8px] text-zinc-500 mt-1">
        <span>Mono</span>
        <span>Stereo</span>
        <span>Wide</span>
      </div>
    </div>
  );
};

// ==================== Export Presets UI ====================

const ExportPresetsUI = ({ 
  selectedPreset, 
  onSelectPreset, 
  onExport,
  isExporting,
  className 
}) => {
  const [showAll, setShowAll] = useState(false);
  
  const mainPresets = ['streaming', 'youtube', 'cd', 'podcast'];
  const allPresets = Object.keys(EXPORT_PRESETS);
  const displayPresets = showAll ? allPresets : mainPresets;
  
  const presetIcons = {
    streaming: Music2,
    youtube: Radio,
    cd: Disc,
    podcast: Headphones,
    hifi: Settings2,
    broadcast: Radio,
    soundcloud: Music2,
    master: Disc,
  };
  
  return (
    <div className={cn("bg-zinc-800/50 rounded-lg p-3", className)}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-white">Export Preset</span>
        <button
          onClick={() => setShowAll(!showAll)}
          className="text-[10px] text-indigo-400 hover:text-indigo-300"
        >
          {showAll ? 'Show Less' : 'Show All'}
        </button>
      </div>
      
      <div className="grid grid-cols-2 gap-2 mb-3">
        {displayPresets.map((presetId) => {
          const preset = EXPORT_PRESETS[presetId];
          const IconComponent = presetIcons[presetId] || Disc;
          const isSelected = selectedPreset === presetId;
          
          return (
            <button
              key={presetId}
              onClick={() => onSelectPreset(presetId)}
              className={cn(
                "p-2 rounded-lg border text-left transition-all",
                isSelected 
                  ? "bg-indigo-500/20 border-indigo-500/50" 
                  : "bg-zinc-900/50 border-zinc-700 hover:border-zinc-600"
              )}
            >
              <div className="flex items-center gap-2">
                <IconComponent className={cn(
                  "w-4 h-4",
                  isSelected ? "text-indigo-400" : "text-zinc-400"
                )} />
                <span className={cn(
                  "text-[10px] font-medium truncate",
                  isSelected ? "text-white" : "text-zinc-300"
                )}>
                  {preset.name}
                </span>
              </div>
              {preset.targetLUFS && (
                <div className="text-[8px] text-zinc-500 mt-1 ml-6">
                  Target: {preset.targetLUFS} LUFS
                </div>
              )}
            </button>
          );
        })}
      </div>
      
      {/* Selected preset details */}
      {selectedPreset && (
        <div className="p-2 bg-zinc-900/50 rounded-lg mb-3">
          <div className="text-[10px] text-zinc-300">
            {EXPORT_PRESETS[selectedPreset]?.description}
          </div>
          <div className="flex gap-3 mt-1 text-[8px] text-zinc-500">
            <span>{EXPORT_PRESETS[selectedPreset]?.sampleRate / 1000}kHz</span>
            <span>{EXPORT_PRESETS[selectedPreset]?.bitDepth}bit</span>
            <span className="uppercase">{EXPORT_PRESETS[selectedPreset]?.format}</span>
          </div>
        </div>
      )}
      
      <Button
        onClick={() => onExport?.(selectedPreset)}
        disabled={!selectedPreset || isExporting}
        className="w-full bg-emerald-500 hover:bg-emerald-600"
      >
        {isExporting ? (
          <>
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            Exporting...
          </>
        ) : (
          <>
            <Download className="w-4 h-4 mr-2" />
            Export with Preset
          </>
        )}
      </Button>
    </div>
  );
};

// ==================== Main Mastering Panel ====================

export const MasteringPanel = ({
  audioBuffer,
  audioContext,
  onExport,
  isExporting,
  onClose,
  isVisible = true,
}) => {
  const [lufsReadings, setLufsReadings] = useState({
    momentary: -Infinity,
    shortTerm: -Infinity,
    integrated: -Infinity,
    truePeak: -Infinity,
  });
  const [selectedPreset, setSelectedPreset] = useState('streaming');
  const [stereoWidth, setStereoWidth] = useState(1.0);
  const [multibandBands, setMultibandBands] = useState([
    { threshold: -24 },
    { threshold: -20 },
    { threshold: -18 },
    { threshold: -16 },
  ]);
  
  const lufsMeterRef = useRef(null);
  
  // Initialize LUFS meter
  useEffect(() => {
    if (audioContext) {
      lufsMeterRef.current = new LUFSMeter(audioContext, audioContext.sampleRate);
    }
    return () => {
      lufsMeterRef.current = null;
    };
  }, [audioContext]);
  
  // Analyze audio buffer for LUFS
  const analyzeBuffer = useCallback(() => {
    if (!audioBuffer || !lufsMeterRef.current) return;
    
    lufsMeterRef.current.reset();
    
    const leftChannel = audioBuffer.getChannelData(0);
    const rightChannel = audioBuffer.numberOfChannels > 1 
      ? audioBuffer.getChannelData(1) 
      : leftChannel;
    
    // Process in chunks
    const chunkSize = 4096;
    for (let i = 0; i < leftChannel.length; i += chunkSize) {
      const left = leftChannel.slice(i, i + chunkSize);
      const right = rightChannel.slice(i, i + chunkSize);
      lufsMeterRef.current.process(left, right);
    }
    
    setLufsReadings(lufsMeterRef.current.getReadings());
  }, [audioBuffer]);
  
  // Analyze on mount and when buffer changes
  useEffect(() => {
    if (audioBuffer) {
      analyzeBuffer();
    }
  }, [audioBuffer, analyzeBuffer]);
  
  const handleMultibandChange = (bandIndex, param, value) => {
    setMultibandBands(prev => {
      const newBands = [...prev];
      newBands[bandIndex] = { ...newBands[bandIndex], [param]: value };
      return newBands;
    });
  };
  
  const targetLUFS = selectedPreset ? EXPORT_PRESETS[selectedPreset]?.targetLUFS : null;
  
  // API Base URL
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  
  // Helper: Convert AudioBuffer to WAV blob
  const audioBufferToWav = async (buffer) => {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1; // PCM
    const bitDepth = 16;
    
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;
    
    const leftChannel = buffer.getChannelData(0);
    const rightChannel = numChannels > 1 ? buffer.getChannelData(1) : leftChannel;
    const length = leftChannel.length;
    
    const dataLength = length * numChannels * bytesPerSample;
    const bufferLength = 44 + dataLength;
    const arrayBuffer = new ArrayBuffer(bufferLength);
    const view = new DataView(arrayBuffer);
    
    // RIFF header
    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataLength, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(36, 'data');
    view.setUint32(40, dataLength, true);
    
    // Write audio data
    let offset = 44;
    for (let i = 0; i < length; i++) {
      const leftSample = Math.max(-1, Math.min(1, leftChannel[i]));
      view.setInt16(offset, leftSample * 0x7fff, true);
      offset += 2;
      
      if (numChannels === 2) {
        const rightSample = Math.max(-1, Math.min(1, rightChannel[i]));
        view.setInt16(offset, rightSample * 0x7fff, true);
        offset += 2;
      }
    }
    
    return new Blob([arrayBuffer], { type: 'audio/wav' });
  };
  
  // Helper: Convert blob to base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };
  
  
  // Use backend analysis when available
  useEffect(() => {
    if (audioBuffer) {
      // Use a flag to avoid race conditions
      let isCancelled = false;
      
      const doAnalysis = async () => {
        try {
          const wavBlob = await audioBufferToWav(audioBuffer);
          const base64 = await blobToBase64(wavBlob);
          
          const response = await fetch(`${API_URL}/api/mastering/analyze`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              audio_data: base64,
              sample_rate: audioBuffer.sampleRate,
              channels: audioBuffer.numberOfChannels
            })
          });
          
          if (!isCancelled && response.ok) {
            const result = await response.json();
            setLufsReadings({
              momentary: result.momentary,
              shortTerm: result.short_term,
              integrated: result.integrated,
              truePeak: result.true_peak,
              loudnessRange: result.loudness_range
            });
          }
        } catch (err) {
          console.error('Backend analysis failed, using client-side:', err);
          if (!isCancelled) {
            analyzeBuffer();
          }
        }
      };
      
      doAnalysis();
      
      return () => {
        isCancelled = true;
      };
    }
  }, [audioBuffer, API_URL, analyzeBuffer]);

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="w-80 bg-zinc-900/95 backdrop-blur-md border-l border-zinc-700 flex flex-col h-full"
      data-testid="mastering-panel"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-800 bg-gradient-to-r from-amber-500/10 to-orange-500/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
            <Gauge className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-white">Mastering</h3>
            <p className="text-xs text-zinc-400">Final polish & export</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-zinc-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* LUFS Meter */}
        <LUFSMeterDisplay 
          readings={lufsReadings} 
          targetLUFS={targetLUFS}
        />
        
        {/* Multi-band Compressor */}
        <MultibandCompressorUI
          bands={multibandBands}
          onChange={handleMultibandChange}
        />
        
        {/* Stereo Imager */}
        <StereoImagerUI
          width={stereoWidth}
          onChange={setStereoWidth}
        />
        
        {/* Export Presets */}
        <ExportPresetsUI
          selectedPreset={selectedPreset}
          onSelectPreset={setSelectedPreset}
          onExport={onExport}
          isExporting={isExporting}
        />
        
        {/* Info */}
        {!audioBuffer && (
          <div className="text-center py-4 text-zinc-500 text-sm">
            <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>Record or import audio to analyze</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default MasteringPanel;
